import React, { useCallback } from 'react';
import styled, { RuleSet } from 'styled-components';
import { Translation } from '~/components/translation/types';
import { RecommendedBetDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import SportsbookSportIcon from '~/components/sportsbook/lobby/components/sportsbook-sport-icon';
import SelectionButton from '~/components/sportsbook/components/selection-button';
import formatOdds from '../../../utils/format-odds';
import { OddsFormat } from '../../../sportsbook-types';
import BetRecommendationsBetItem from './sportsbook-bet-recommendations-bet-item';
import {
	BetRecommendationsBulletConnectedList,
	BetRecommendationsBulletConnectedListItem
} from './sportsbook-bet-recommendations-bullet-connected-list';
import multiply from '../../../utils/muiltiply';
import BetRecommendationsBetTitle from './sportsbook-bet-recommendations-bet-title';
import OddsChangeCounter from '~/components/sportsbook/lobby/components/odds-change-counter';
import useOddsChangeStyles from '~/components/sportsbook/lobby/hooks/use-odds-change-styles';
import normalizeOdds from '../../../utils/normalize-odds';

const BetInnerWrapper = styled.div<{ $isInvisible: boolean }>`
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-items: flex-start;
	align-items: stretch;
	${({ $isInvisible }) => $isInvisible && 'visibility: hidden;'}
`;

const BetHeader = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-start;
	gap: 5px;
	background-image: linear-gradient(to right, var(--main-surface-1), var(--surface-to-primary-gradient));
`;

const StyledSelectionButton = styled(SelectionButton)<{ oddsChangeStyles?: RuleSet }>`
	${({ oddsChangeStyles }) => oddsChangeStyles}
`;

const BetTitleWrapper = styled.div`
	padding: 12px 0px 12px 12px;
	flex: 0 1 auto;
	overflow: hidden;
`;

const BetIconWrapper = styled.div`
	background-color: var(--main-secondary);
	border-bottom-left-radius: 100%;
	// Sizing
	height: 38px;
	aspect-ratio: 1;
	// Positioning of icon
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	padding-top: 6px;
	padding-right: 6px;
`;

const BetIcon = styled(SportsbookSportIcon)`
	color: var(--main-on-secondary);
	height: 20px;
	width: 20px;
`;

const ContentWrapper = styled.div`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: stretch;
	padding: 12px;
	gap: 12px;
	background-image: linear-gradient(to bottom, var(--surface-to-primary-gradient), var(--main-surface-1));
`;

type BetRecommendationsBetProps = {
	translations: Translation;
	bet: RecommendedBetDto;
	oddsFormat: OddsFormat;
	isActive: boolean;
	isInvisible?: boolean;
	shouldDeepLink?: boolean;
	onClick: () => void;
};

const BetRecommendationsBet: React.FC<BetRecommendationsBetProps> = ({
	translations,
	bet,
	oddsFormat,
	isActive,
	isInvisible = false,
	shouldDeepLink = true,
	onClick
}) => {
	const totalOdds = bet.items.reduce((result, item) => multiply(result, item.selection.trueOdds), 1);
	const normalizedTotalOdds = normalizeOdds(totalOdds);

	const renderOdds = useCallback((odds: number) => formatOdds(odds, oddsFormat), [oddsFormat]);
	const oddsChangeStyles = useOddsChangeStyles(normalizedTotalOdds);

	// Set sportId to value if all items event has the same sportId, otherwise undefined
	const sportIcon = bet.items.every(item => item.event.sportId === bet.items[0].event.sportId)
		? bet.items[0].event.metadata.sportIcon
		: undefined;

	const isCombo = bet.items.length > 1;
	const isSinglePreCanned = !isCombo && bet.items[0].market.marketType.isPreCanned;

	return (
		<BetInnerWrapper $isInvisible={isInvisible}>
			<BetHeader>
				<BetTitleWrapper>
					<BetRecommendationsBetTitle bet={bet} shouldDeepLink={shouldDeepLink} translations={translations} />
				</BetTitleWrapper>
				<BetIconWrapper>
					<BetIcon icon={sportIcon} />
				</BetIconWrapper>
			</BetHeader>
			<ContentWrapper>
				<BetRecommendationsBulletConnectedList gap="12px">
					{bet.items.map((item, index) => (
						<BetRecommendationsBulletConnectedListItem
							key={item.selection.id}
							showBullet={isCombo}
							isLast={index === bet.items.length - 1}
						>
							<BetRecommendationsBetItem
								event={item.event}
								market={item.market}
								selection={item.selection}
								shouldDisplayEventName={!isSinglePreCanned}
								shouldDeepLink={shouldDeepLink}
								translations={translations}
							/>
						</BetRecommendationsBulletConnectedListItem>
					))}
				</BetRecommendationsBulletConnectedList>
				<StyledSelectionButton onClick={onClick} isActive={isActive} oddsChangeStyles={oddsChangeStyles}>
					<OddsChangeCounter odds={normalizedTotalOdds} renderOdds={renderOdds} />
				</StyledSelectionButton>
			</ContentWrapper>
		</BetInnerWrapper>
	);
};

export default BetRecommendationsBet;
